/**
 * Single fetch wrapper for the same-origin API.
 * - Attaches the anti-forgery token (delivered by /api/users/me) on mutating requests.
 * - Normalises errors to ApiError with the server's ProblemDetails payload.
 * - 401 → emits an 'pb:unauthorised' event the auth context listens for.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly problem: Record<string, unknown> | null;

  constructor(status: number, problem: Record<string, unknown> | null) {
    super(typeof problem?.title === 'string' ? problem.title : `API error ${status}`);
    this.status = status;
    this.problem = problem;
  }

  /** Machine-readable error code (e.g. "step-up-required", "mfa-enrolment-required"). */
  get code(): string | null {
    return typeof this.problem?.code === 'string' ? this.problem.code : null;
  }
}

/** Flattens RFC-7807 validation problem `errors` into a single message for form banners. */
export function formatFieldErrors(err: unknown, fallback = 'Something went wrong — please try again.'): string {
  if (err instanceof ApiError && err.problem?.errors) {
    const messages = Object.values(err.problem.errors as Record<string, string[]>).flat();
    if (messages.length > 0) return messages.join(' ');
  }
  return fallback;
}

let csrfToken: string | null = null;

export function setCsrfToken(token: string): void {
  csrfToken = token;
}

/** For the rare non-JSON call (multipart upload) that must attach the header itself. */
export function getCsrfToken(): string | null {
  return csrfToken;
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  return apiCore<T>(path, init, true);
}

async function apiCore<T>(path: string, init: RequestInit, allowCsrfRetry: boolean): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  if (init.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (MUTATING.has(method) && csrfToken) {
    headers.set('X-CSRF-TOKEN', csrfToken);
  }

  const res = await fetch(path, { ...init, method, headers, credentials: 'same-origin' });

  // 401s from the auth endpoints themselves are expected outcomes (wrong password, bad TOTP
  // code) — they must not tear down a session that is still valid elsewhere in the app.
  if (res.status === 401 && !path.startsWith('/api/auth/')) {
    window.dispatchEvent(new CustomEvent('pb:unauthorised'));
  }

  if (!res.ok) {
    let problem: Record<string, unknown> | null = null;
    try {
      problem = (await res.json()) as Record<string, unknown>;
    } catch {
      /* non-JSON error body */
    }

    // Self-heal a stale anti-forgery token (old tab, auth transition, server restart):
    // fetch a fresh token and retry the request exactly once.
    if (
      allowCsrfRetry &&
      res.status === 400 &&
      MUTATING.has(method) &&
      typeof problem?.title === 'string' &&
      problem.title.toLowerCase().includes('anti-forgery')
    ) {
      try {
        const csrfRes = await fetch('/api/auth/csrf', { credentials: 'same-origin' });
        if (csrfRes.ok) {
          const { token } = (await csrfRes.json()) as { token: string };
          setCsrfToken(token);
          return apiCore<T>(path, init, false);
        }
      } catch {
        /* fall through to the original error */
      }
    }

    throw new ApiError(res.status, problem);
  }

  // Endpoints like register/logout return 200 with an empty body — res.json() would throw.
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}
